import { CATEGORY_LABEL } from '../content/attractionMeta'
import { questIntro } from '../content/narration'
import { estimateUsage, getThumbUrl } from '../photoStore'
import type { DiscoveryRecord } from '../questState'
import type { QuestProgress } from '../quests'
import type { Attraction, HistoryEvent, Quest, Settlement } from '../types'

export type AtlasTab = 'places' | 'history' | 'quests'

export interface AtlasApi {
  open: (tab?: AtlasTab) => void
  close: () => void
  isOpen: () => boolean
}

/** Bumped on every list paint so in-flight thumb hydration can abandon stale DOM writes. */
let renderToken = 0

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(0, Math.round(bytes / 1024))} KB`
}

function unverifiedChip(): HTMLElement {
  const chip = element('span', 'wa-chip wa-chip-warn', 'Unverified')
  chip.title = 'Awaiting local review — not yet used for guides or narration.'
  return chip
}

function statusChip(record: DiscoveryRecord | undefined): HTMLElement | null {
  if (!record) return null
  if (record.photographedAt) {
    return element('span', 'wa-chip wa-chip-shot', 'Photographed')
  }
  return element('span', 'wa-chip wa-chip-seen', 'Seen')
}

function thumbSlot(
  id: string,
  alt: string,
  onPhotoOpen: (id: string, title: string) => void,
): HTMLImageElement {
  const img = element('img', 'wa-thumb')
  img.alt = alt
  img.width = 48
  img.height = 48
  img.dataset.photoId = id
  img.addEventListener('click', (event) => {
    event.stopPropagation()
    onPhotoOpen(id, alt)
  })
  return img
}

function setCountLines(count: HTMLElement, primary: string, discovered: string) {
  count.replaceChildren()
  count.append(document.createTextNode(primary))
  count.append(document.createElement('br'))
  count.append(document.createTextNode(discovered))
}

export function bindAtlas(input: {
  attractions: Attraction[]
  settlements: Settlement[]
  history: HistoryEvent[]
  quests: Quest[]
  questProgress: () => QuestProgress[]
  getRecord: (id: string) => DiscoveryRecord | undefined
  counts: () => { discovered: number; photographed: number; total: number }
  onFocus: (target: {
    name: string
    location: { lat: number; lon: number }
    tier?: Attraction['tier']
  }) => void
  onPhotoOpen: (id: string, title: string) => void
  onTab?: (tab: AtlasTab) => void
  onExpand?: (kind: 'attraction' | 'history', id: string) => void
  onClose?: () => void
}): AtlasApi {
  const root = document.querySelector<HTMLElement>('#world-atlas')!
  const list = document.querySelector<HTMLElement>('#wa-list')!
  const closeBtn = document.querySelector<HTMLButtonElement>('#wa-close')!
  const tabPlaces = document.querySelector<HTMLButtonElement>('#wa-tab-places')!
  const tabHistory = document.querySelector<HTMLButtonElement>('#wa-tab-history')!
  const tabQuests = document.querySelector<HTMLButtonElement>('#wa-tab-quests')!
  const count = document.querySelector<HTMLElement>('#wa-count')!

  let tab: AtlasTab = 'places'

  const hydrateThumbs = async (token: number) => {
    const slots = list.querySelectorAll<HTMLImageElement>('img.wa-thumb[data-photo-id]')
    for (const img of slots) {
      const id = img.dataset.photoId
      if (!id) continue
      const url = await getThumbUrl(id)
      if (token !== renderToken) return
      if (url) img.src = url
    }
  }

  const hydrateStorage = async (token: number) => {
    const usage = await estimateUsage()
    if (token !== renderToken || !usage) return
    count.append(document.createElement('br'))
    const label = usage.photos === 1 ? 'photo' : 'photos'
    count.append(
      document.createTextNode(`${usage.photos} ${label} · ${formatBytes(usage.usage)}`),
    )
  }

  const addFlyButton = (
    into: HTMLElement,
    label: string,
    target: Parameters<typeof input.onFocus>[0],
  ) => {
    const fly = element('button', 'wa-fly', label)
    fly.type = 'button'
    fly.addEventListener('click', () => input.onFocus(target))
    into.append(fly)
  }

  const renderAttraction = (attraction: Attraction): HTMLElement => {
    const item = element('div', `wa-item wa-${attraction.tier}`)
    const record = input.getRecord(attraction.id)

    const head = element('button', 'wa-head')
    head.type = 'button'
    head.setAttribute('aria-expanded', 'false')
    if (record?.photographedAt) {
      head.append(thumbSlot(attraction.id, attraction.name, input.onPhotoOpen))
    }
    const heading = element('span', 'wa-head-main')
    heading.append(element('strong', undefined, attraction.name))
    heading.append(element('span', 'wa-summary', attraction.summary))
    head.append(heading)
    const meta = element('span', 'wa-meta')
    meta.append(element('span', 'wa-chip', CATEGORY_LABEL[attraction.category]))
    const status = statusChip(record)
    if (status) meta.append(status)
    if (attraction.needsVerification) meta.append(unverifiedChip())
    head.append(meta)

    const detail = element('div', 'wa-detail')
    detail.hidden = true
    if (attraction.howItWorks) {
      detail.append(element('p', 'wa-how', attraction.howItWorks))
    }
    if (attraction.discover?.length) {
      detail.append(element('p', 'wa-detail-label', 'What you will notice'))
      const ul = element('ul', 'wa-discover')
      for (const line of attraction.discover) ul.append(element('li', undefined, line))
      detail.append(ul)
    }
    if (attraction.traversal?.length) {
      const modes = element('p', 'wa-traversal', `How you move: ${attraction.traversal.join(' · ')}`)
      detail.append(modes)
    }
    const actions = element('div', 'wa-actions')
    addFlyButton(actions, 'Fly here', {
      name: attraction.name,
      location: attraction.location,
      tier: attraction.tier,
    })
    detail.append(actions)

    head.addEventListener('click', () => {
      const opening = detail.hidden
      detail.hidden = !opening
      head.setAttribute('aria-expanded', String(opening))
      item.classList.toggle('open', opening)
      if (opening) input.onExpand?.('attraction', attraction.id)
    })

    item.append(head, detail)
    return item
  }

  const renderSettlement = (settlement: Settlement): HTMLElement => {
    const item = element('div', 'wa-item wa-minor')
    const record = input.getRecord(settlement.id)
    const head = element('div', 'wa-head wa-head-static')
    if (record?.photographedAt) {
      head.append(thumbSlot(settlement.id, settlement.name, input.onPhotoOpen))
    }
    const heading = element('span', 'wa-head-main')
    heading.append(element('strong', undefined, settlement.name))
    heading.append(element('span', 'wa-summary', settlement.summary))
    head.append(heading)
    const meta = element('span', 'wa-meta')
    meta.append(element('span', 'wa-chip', settlement.kind))
    const status = statusChip(record)
    if (status) meta.append(status)
    if (settlement.needsVerification) meta.append(unverifiedChip())
    head.append(meta)
    const actions = element('div', 'wa-actions wa-actions-inline')
    addFlyButton(actions, 'Fly here', {
      name: settlement.name,
      location: settlement.location,
    })
    item.append(head, actions)
    return item
  }

  const discoveredLine = () => {
    const { discovered, total } = input.counts()
    return `Discovered ${discovered} / ${total}`
  }

  const renderPlaces = () => {
    const token = ++renderToken
    const major = input.attractions.filter((a) => a.tier === 'major')
    const minor = input.attractions.filter((a) => a.tier === 'minor')
    const nodes: HTMLElement[] = []
    const { photographed } = input.counts()

    nodes.push(element('h3', 'wa-group', `Major attractions · ${major.length}`))
    for (const attraction of major) nodes.push(renderAttraction(attraction))

    nodes.push(element('h3', 'wa-group', `Worth a detour · ${minor.length}`))
    for (const attraction of minor) nodes.push(renderAttraction(attraction))

    nodes.push(element('h3', 'wa-group', `Towns and settlements · ${input.settlements.length}`))
    for (const settlement of input.settlements) nodes.push(renderSettlement(settlement))

    list.replaceChildren(...nodes)
    setCountLines(
      count,
      `${input.attractions.length} places · ${input.settlements.length} settlements · ${photographed} photographed`,
      discoveredLine(),
    )
    void hydrateThumbs(token)
    void hydrateStorage(token)
  }

  const renderHistory = () => {
    const token = ++renderToken
    const nodes: HTMLElement[] = []
    nodes.push(
      element(
        'p',
        'wa-lede',
        'Arenal was inhabited, farmed and mapped long before it was a destination. The timeline runs oldest first.',
      ),
    )
    for (const event of input.history) {
      const item = element('div', 'wa-item wa-event')
      const record = input.getRecord(event.id)
      const head = element('button', 'wa-head')
      head.type = 'button'
      head.setAttribute('aria-expanded', 'false')
      if (record?.photographedAt) {
        head.append(thumbSlot(event.id, event.title, input.onPhotoOpen))
      }
      const heading = element('span', 'wa-head-main')
      heading.append(element('span', 'wa-when', event.when))
      heading.append(element('strong', undefined, event.title))
      heading.append(element('span', 'wa-summary', event.summary))
      head.append(heading)
      const meta = element('span', 'wa-meta')
      const status = statusChip(record)
      if (status) meta.append(status)
      if (event.needsVerification) meta.append(unverifiedChip())
      if (meta.childElementCount > 0) head.append(meta)

      const detail = element('div', 'wa-detail')
      detail.hidden = true
      if (event.body) detail.append(element('p', 'wa-how', event.body))
      if (event.sources.length) {
        detail.append(element('p', 'wa-detail-label', 'Sources'))
        const ul = element('ul', 'wa-sources')
        for (const source of event.sources) {
          const li = element('li')
          const link = element('a', undefined, new URL(source).hostname.replace(/^www\./, ''))
          link.href = source
          link.target = '_blank'
          link.rel = 'noopener'
          li.append(link)
          ul.append(li)
        }
        detail.append(ul)
      }

      head.addEventListener('click', () => {
        const opening = detail.hidden
        detail.hidden = !opening
        head.setAttribute('aria-expanded', String(opening))
        item.classList.toggle('open', opening)
        if (opening) input.onExpand?.('history', event.id)
      })

      item.append(head, detail)
      nodes.push(item)
    }
    list.replaceChildren(...nodes)
    setCountLines(count, `${input.history.length} events`, discoveredLine())
    void hydrateThumbs(token)
  }

  const renderQuest = (quest: Quest, progress: QuestProgress): HTMLElement => {
    const item = element('div', `wa-item wa-quest${progress.complete ? ' wa-quest-done' : ''}`)
    const head = element('div', 'wa-head wa-head-static')
    const heading = element('span', 'wa-head-main')
    heading.append(element('strong', undefined, quest.title))
    // The elder's phrasing when he has one, so the Atlas echoes the voice that
    // handed the quest over rather than restating it in the narrator's.
    heading.append(element('span', 'wa-summary', questIntro(quest.id) ?? quest.premise))
    head.append(heading)
    item.append(head)

    const steps = element('ul', 'wa-steps')
    for (const step of progress.steps) {
      const li = element('li', `wa-step${step.done ? ' done' : ''}`)
      li.append(element('span', 'wa-check', step.done ? '✓' : ''))
      const label = element('span', 'wa-step-text', step.text)
      li.append(label)
      if (!step.done && step.need > 1) {
        li.append(element('span', 'wa-step-count', `${step.have} / ${step.need}`))
      }
      steps.append(li)
    }
    item.append(steps)

    if (progress.complete) {
      item.append(element('p', 'wa-reward', quest.reward.text))
    } else {
      const teaser =
        quest.reward.type === 'lore' ? 'Reward: Lore' : `Reward: ${quest.reward.text}`
      item.append(element('p', 'wa-reward wa-reward-teaser', teaser))
    }
    return item
  }

  const renderQuests = () => {
    renderToken++
    const progress = input.questProgress()
    const byId = new Map(progress.map((entry) => [entry.questId, entry]))
    const nodes: HTMLElement[] = []
    for (const quest of input.quests) {
      const entry = byId.get(quest.id)
      if (!entry) continue
      nodes.push(renderQuest(quest, entry))
    }
    list.replaceChildren(...nodes)
    const complete = progress.filter((entry) => entry.complete).length
    count.textContent = `${complete} / ${input.quests.length} quests complete`
  }

  const setTab = (next: AtlasTab) => {
    tab = next
    tabPlaces.classList.toggle('active', tab === 'places')
    tabPlaces.setAttribute('aria-selected', String(tab === 'places'))
    tabHistory.classList.toggle('active', tab === 'history')
    tabHistory.setAttribute('aria-selected', String(tab === 'history'))
    tabQuests.classList.toggle('active', tab === 'quests')
    tabQuests.setAttribute('aria-selected', String(tab === 'quests'))
    list.scrollTop = 0
    if (tab === 'places') renderPlaces()
    else if (tab === 'history') renderHistory()
    else renderQuests()
    input.onTab?.(tab)
  }

  const close = () => {
    const wasOpen = !root.classList.contains('hidden')
    root.classList.add('hidden')
    root.setAttribute('aria-hidden', 'true')
    root.inert = true
    if (wasOpen) input.onClose?.()
  }

  const open = (next?: AtlasTab) => {
    setTab(next ?? tab)
    root.classList.remove('hidden')
    root.removeAttribute('aria-hidden')
    root.inert = false
  }

  closeBtn.addEventListener('click', close)
  tabPlaces.addEventListener('click', () => setTab('places'))
  tabHistory.addEventListener('click', () => setTab('history'))
  tabQuests.addEventListener('click', () => setTab('quests'))
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !root.classList.contains('hidden')) close()
  })

  close()
  return { open, close, isOpen: () => !root.classList.contains('hidden') }
}
